import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from "axios";
import { isInRenderMode } from "./renderModeFlag";

// API base URL - should be configured via environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/lis`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    if (isInRenderMode()) {
      if (config.headers) {
        delete config.headers.Authorization;
        delete (config.headers as any).common?.Authorization;
      }
      return config;
    }
    const token = localStorage.getItem("token");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
apiClient.interceptors.response.use(
  (response) => response,
  (
    error: AxiosError<{
      message?: string;
      code?: string;
      requiresPassword?: boolean;
      error?: string;
    }>
  ) => {
    if (error.response?.status === 401) {
      // Check if this is a sign-off authentication error (not token expiry)
      // Sign-off auth errors have requiresPassword flag or are from sign-off endpoints
      const isSignOffAuth =
        error.response?.data?.requiresPassword ||
        error.config?.url?.includes("sign-off") ||
        error.config?.url?.includes("/amendment");

      const isLoginAttempt = error.config?.url?.includes("/auth/login");

      if (!isSignOffAuth && !isLoginAttempt && !isInRenderMode()) {
        // Token expired or invalid - clear auth and redirect to login.
        // Skip when in headless render mode: a 401 on a non-essential endpoint
        // (e.g. /admin/report-templates/active) must not navigate the page away.
        localStorage.removeItem("token");
        localStorage.removeItem("auth-storage");
        window.location.href = "/login";
      }
      // For sign-off auth errors, don't redirect - let the calling code handle it
    }

    // Extract user-friendly message from API response body
    // Backend badRequest() returns { error: "..." }, other responses may use { message: "..." }
    const apiMessage =
      error.response?.data?.message || error.response?.data?.error;
    if (apiMessage) {
      const customError = new Error(apiMessage);
      (customError as any).response = error.response;
      (customError as any).code = error.response?.data?.code;
      return Promise.reject(customError);
    }

    return Promise.reject(error);
  }
);

// Helper function to set auth token
export const setAuthToken = (token: string | null) => {
  if (token) {
    localStorage.setItem("token", token);
    apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem("token");
    delete apiClient.defaults.headers.common.Authorization;
  }
};

// Type-safe request methods
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, data, config).then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, data, config).then((res) => res.data),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config).then((res) => res.data),
};

export default apiClient;
